import { useQuery } from "@tanstack/react-query";
import { getMatches } from "@/lib/api";
import { useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";

/** Matches list for the active team — across ALL its seasons.
 *
 * `getMatches()` fetches team-wide (it suppresses `season_id`), so
 * the result depends only on `teamId`, not `seasonId`. The query
 * key is still `useScopedKey("matches")` (the `[domain, teamId,
 * seasonId]` tuple) so it stays compatible with the existing
 * `invalidateScope` / `removeAllScopedFromCache` predicates — a
 * season swap just re-fetches the same team-wide list into a new
 * cache bucket, which is a minor efficiency wrinkle, not a
 * correctness issue. (If matches ever need a season-scoped variant,
 * add a separate verb + key rather than re-narrowing this one.)
 *
 * `enabled: !!teamId` gates the fetch until scope is set — prevents
 * the 409 "Team selection required" round-trip on first paint when
 * the user has multiple teams.
 */
export function useMatches() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery({
    queryKey: useScopedKey("matches"),
    queryFn: getMatches,
    enabled: !!teamId,
  });
}
